<script>
  /**
   * One client's live-parity page: wallet (no secrets), balance, bets, strategies.
   * Data root is that client's CSV/yaml — never Georg live.
   */
  import { onMount, onDestroy } from 'svelte'
  import {
    getClientAdminAccount,
    clientAdminEmergency,
    clientAdminPasswordReset,
    setClientStrategyEnabled,
    setClientTradingEnabled,
  } from '../../api.js'
  import { link } from '../../lib/historyNav.js'
  import { shortAddress } from '../../mode.js'
  import { HORIZON_TITLES } from '../../lib/horizon.js'
  import { fmtBankroll, fmtNum, fmtWinRate, OPS_ERROR_RU } from '../../lib/format.js'
  import { formatPnlPct, monthOfDay, mskToday } from '../../lib/tradingDay.js'
  import BetFeed from '../../components/BetFeed.svelte'
  import DayPicker from '../../components/DayPicker.svelte'

  export let telegramId = 0

  const HZ = '15m'
  const POLL_MS = 20000

  let acc = null
  let err = ''
  let msg = ''
  let msgKind = ''
  let busy = ''
  let confirmStop = false
  let yamlPending = 'появятся после клона live / ключей'
  let selectedDay = ''
  let liveMonth = ''
  let dayBusy = false
  let pollTimer = null
  let pendingArmed = null

  $: live = (acc && acc.live) || {}
  $: balance = live.balance || {}
  $: liveBets = live.bets || {}
  $: stratRows = (live.strategies && live.strategies.strategies) || []
  $: stratSource = (live.strategies && live.strategies.source) || ''
  $: yamlOk = !!(acc && acc.strategies && acc.strategies.toggle_allowed)
  $: wallet = (acc && acc.wallet) || {}
  $: trading = (acc && acc.trading) || {}
  $: serverArmed = !!(trading.armed)
  $: tradeArmed = pendingArmed != null ? pendingArmed : serverArmed
  $: tradeCanOn = !!(trading.can_go_live)
  $: tradeUnitActive = trading.unit_active === true
  $: tradeHint = trading.note_ru || ''
  $: riskNote = (acc && acc.risk_note_ru) || trading.risk_note_ru || ''

  $: onchainBal =
    balance.onchain_pusd != null && Number.isFinite(Number(balance.onchain_pusd))
      ? Number(balance.onchain_pusd)
      : null
  $: onchainPol =
    balance.onchain_pol != null && Number.isFinite(Number(balance.onchain_pol))
      ? Number(balance.onchain_pol)
      : null
  $: equity =
    balance.equity != null && Number.isFinite(Number(balance.equity))
      ? Number(balance.equity)
      : onchainBal
  $: bank = equity != null ? fmtBankroll({ balance: equity }) : null
  $: bDay =
    balance.b_day != null && Number.isFinite(Number(balance.b_day))
      ? Number(balance.b_day)
      : null
  $: floor =
    balance.min_bankroll_floor != null && Number.isFinite(Number(balance.min_bankroll_floor))
      ? Number(balance.min_bankroll_floor)
      : null

  $: balanceSub = (() => {
    const parts = []
    const holder = balance.pusd_holder || wallet.funder || wallet.eoa
    if (holder) parts.push(shortAddress(holder))
    if (onchainBal != null && balance.onchain_pusd_ok) {
      parts.push(wallet.funder ? 'deposit pUSD' : 'on-chain pUSD')
    } else if (balance.source === 'client_live_bankroll') {
      parts.push('из live_bankroll клиента')
    } else if (!holder) {
      parts.push('кошелёк не задан')
    }
    if (bDay != null) parts.push(`B_day ${fmtNum(bDay)}`)
    if (floor != null) parts.push(`floor ${fmtNum(floor)}`)
    return parts.join(' · ')
  })()

  $: polDisplay =
    onchainPol != null ? (onchainPol >= 10 ? onchainPol.toFixed(2) : onchainPol.toFixed(4)) : null
  $: polSub = (() => {
    const parts = []
    if (wallet.eoa) parts.push(shortAddress(wallet.eoa))
    if (onchainPol != null && balance.onchain_pol_ok) parts.push('native POL · газ')
    else parts.push('RPC нет')
    return parts.join(' · ')
  })()

  $: recent = (liveBets.bets && liveBets.bets.length ? liveBets.bets : []).slice(0, 50)
  $: betCount =
    liveBets.bet_count != null && Number.isFinite(Number(liveBets.bet_count))
      ? Number(liveBets.bet_count)
      : null
  $: openStake =
    liveBets.open_stake != null && Number.isFinite(Number(liveBets.open_stake))
      ? Number(liveBets.open_stake)
      : null
  $: liveGlanceToday = {
    wins: liveBets.wins_today != null ? liveBets.wins_today : liveBets.wins,
    losses: liveBets.losses_today != null ? liveBets.losses_today : liveBets.losses,
    win_rate: liveBets.win_rate_today != null ? liveBets.win_rate_today : liveBets.win_rate,
    roi_weighted: liveBets.roi_weighted_today,
  }
  $: liveGlanceAll = {
    wins: liveBets.wins_all,
    losses: liveBets.losses_all,
    win_rate: liveBets.win_rate_all,
    roi_weighted: liveBets.roi_weighted_all,
  }
  $: cal = liveBets.calendar || null
  $: calDays = (cal && cal.days) || {}
  $: todayId = (cal && cal.today) || liveBets.today || mskToday()
  $: daySummary = liveBets.day_summary || null
  $: dayPctLabel =
    daySummary && daySummary.pnl_pct != null ? formatPnlPct(daySummary.pnl_pct) : ''
  $: pickDay = selectedDay || liveBets.day || todayId

  $: enabledCount = stratRows.filter(
    (r) => r.enabled !== false && !r.unattributed && r.id !== '_unattributed',
  ).length
  $: rosterCount = stratRows.filter((r) => !r.unattributed && r.id !== '_unattributed').length
  $: totalBets = stratRows.reduce((s, r) => s + (Number(r.bets) || 0), 0)
  $: totalSettled = stratRows.reduce((s, r) => s + (Number(r.n) || 0), 0)

  $: modeLabel = (() => {
    if (!acc) return '—'
    if (acc.imitation) return 'имитация'
    const m = String(acc.trading_mode || '')
    if (m === 'live') return 'live'
    if (m === 'disabled') return 'выкл'
    return m || '—'
  })()

  $: statusLine = (() => {
    if (!acc) return 'загрузка'
    const bits = [modeLabel]
    if (acc.imitation) bits.push('без CLOB')
    else bits.push(acc.waitlisted ? 'белый список' : 'отозван')
    if (live.kill_switch_active) bits.push('kill switch')
    if (!acc.imitation) bits.push(acc.secrets_present ? 'секреты есть' : 'секретов нет')
    return bits.join(' · ')
  })()

  function errRu(code, fallback) {
    if (!code) return fallback || 'ошибка'
    return OPS_ERROR_RU[code] || fallback || code
  }

  function fmtRoiWeighted(v) {
    const n = Number(v)
    if (v == null || !Number.isFinite(n)) return null
    const pct = n * 100
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`
  }

  function hitTxt(row) {
    if (row == null || row.hit_pct == null || !row.n) return '—'
    return `${row.hit_pct}%`
  }

  function pnlTxt(row) {
    if (row == null || row.pnl == null) return '—'
    const v = Number(row.pnl)
    if (!Number.isFinite(v)) return '—'
    const sign = v > 0 ? '+' : ''
    return `${sign}${v.toFixed(2)}`
  }

  function stakeTxt(row) {
    if (row == null || row.stake == null || !row.bets) return '—'
    const v = Number(row.stake)
    if (!Number.isFinite(v) || v === 0) return '—'
    return `$${v.toFixed(0)}`
  }

  function statusTxt(row) {
    if (row && row.status) return row.status
    if (row && row.enabled === true) return 'вкл'
    if (row && row.enabled === false) return 'выкл'
    return '—'
  }

  function isEnabled(row) {
    return row != null && row.enabled === true
  }

  function wrapAddr(raw) {
    const s = String(raw || '').trim()
    return s || '—'
  }

  async function load({ day, month } = {}) {
    const tid = Number(telegramId)
    if (!tid) {
      err = 'Неверный telegram_id'
      return
    }
    err = ''
    try {
      const d = day || selectedDay || ''
      const m = month || liveMonth || (d ? monthOfDay(d) : '')
      const { res, data, unauthorized } = await getClientAdminAccount(tid, { day: d, month: m })
      if (unauthorized) return
      if (!res?.ok || !data?.ok) {
        err = errRu(data && data.error, 'Не удалось загрузить клиента')
        acc = null
        return
      }
      acc = data.account
      yamlPending = data.yaml_pending_ru || yamlPending
      const bets = (data.account && data.account.live && data.account.live.bets) || {}
      if (bets.day && !selectedDay) selectedDay = String(bets.day)
      if (bets.calendar && bets.calendar.month) liveMonth = bets.calendar.month
    } catch {
      err = 'Сеть / API недоступны'
    }
  }

  async function onDayChange(e) {
    const day = e.detail && e.detail.day
    if (!day || day === selectedDay) return
    selectedDay = day
    liveMonth = monthOfDay(day)
    dayBusy = true
    try {
      await load({ day, month: liveMonth })
    } finally {
      dayBusy = false
    }
  }

  async function onMonthChange(e) {
    const month = e.detail && e.detail.month
    if (!month) return
    liveMonth = month
    dayBusy = true
    try {
      await load({ day: selectedDay || pickDay, month })
    } finally {
      dayBusy = false
    }
  }

  async function onEmergency() {
    if (!acc || acc.imitation || busy) return
    if (!confirmStop) {
      confirmStop = true
      msg = `Ещё раз нажмите, чтобы отозвать ${acc.display_name || telegramId} с белого списка и остановить торговлю.`
      msgKind = 'pending'
      return
    }
    busy = 'em'
    msg = ''
    try {
      const r = await clientAdminEmergency(acc.telegram_id)
      if (!r.ok) {
        msg = 'Ошибка: ' + errRu(r.error, r.error)
        msgKind = 'bad'
      } else {
        msg = (r.data && r.data.note_ru) || 'Остановлен и отозван с белого списка'
        msgKind = 'ok'
        confirmStop = false
        await load()
      }
    } catch {
      msg = 'Сеть / API недоступны'
      msgKind = 'bad'
    } finally {
      busy = ''
    }
  }

  async function onResetPassword() {
    if (!acc || busy) return
    busy = 'pw'
    msg = ''
    try {
      const r = await clientAdminPasswordReset(acc.telegram_id)
      if (!r.ok) {
        msg = 'Ошибка: ' + errRu(r.error, r.error)
        msgKind = 'bad'
      } else {
        msg = (r.data && r.data.note_ru) || 'Хеш пароля сброшен'
        msgKind = 'ok'
        await load()
      }
    } catch {
      msg = 'Сеть / API недоступны'
      msgKind = 'bad'
    } finally {
      busy = ''
    }
  }

  async function onToggle(row) {
    if (!acc || !row || !yamlOk || acc.imitation || busy) return
    const next = !isEnabled(row)
    busy = `st-${row.id}`
    msg = ''
    try {
      const r = await setClientStrategyEnabled(acc.telegram_id, row.id, next)
      if (!r.ok) {
        msg = 'Ошибка: ' + errRu(r.error, (r.data && r.data.note_ru) || r.error)
        msgKind = 'bad'
      } else {
        msg = (r.data && r.data.note_ru) || (next ? 'Стратегия включена' : 'Стратегия выключена')
        msgKind = 'ok'
        await load()
      }
    } catch {
      msg = 'Сеть / API недоступны'
      msgKind = 'bad'
    } finally {
      busy = ''
    }
  }

  async function onTradeToggle() {
    if (!acc || acc.imitation || busy) return
    const next = !tradeArmed
    if (next && !tradeCanOn) {
      msg = 'Без ключей live не включается'
      msgKind = 'bad'
      return
    }
    busy = 'trade'
    pendingArmed = next
    msg = ''
    try {
      const r = await setClientTradingEnabled(acc.telegram_id, next)
      if (!r.ok) {
        pendingArmed = null
        msg = 'Ошибка: ' + errRu(r.error, r.error_ru || (r.data && r.data.note_ru) || r.error)
        msgKind = 'bad'
        if (r.data && r.data.account) acc = r.data.account
      } else {
        msg = (r.data && r.data.note_ru) || (next ? 'Торговля включена' : 'Торговля выключена')
        msgKind = 'ok'
        if (r.data && r.data.account) acc = r.data.account
        pendingArmed = null
        await load()
      }
    } catch {
      pendingArmed = null
      msg = 'Сеть / API недоступны'
      msgKind = 'bad'
    } finally {
      busy = ''
    }
  }

  async function onStartUnit() {
    if (!acc || acc.imitation || busy || !tradeArmed) return
    busy = 'trade'
    msg = ''
    try {
      const r = await setClientTradingEnabled(acc.telegram_id, true)
      if (!r.ok) {
        msg = 'Ошибка: ' + errRu(r.error, r.error_ru || (r.data && r.data.note_ru) || r.error)
        msgKind = 'bad'
      } else {
        msg = (r.data && r.data.note_ru) || 'Юнит запущен'
        msgKind = 'ok'
        await load()
      }
    } catch {
      msg = 'Сеть / API недоступны'
      msgKind = 'bad'
    } finally {
      busy = ''
    }
  }

  onMount(() => {
    load()
    pollTimer = setInterval(() => load(), POLL_MS)
  })

  onDestroy(() => {
    if (pollTimer) clearInterval(pollTimer)
  })
</script>

<div class="dash client-detail" aria-label="Клиент live">
  <p class="client-detail__back">
    <a href="/dashboard/client" use:link>← Все клиенты</a>
  </p>

  {#if err}
    <p class="pm-error">{err}</p>
  {/if}
  {#if msg}
    <p class="pm-ops-msg pm-ops-msg--{msgKind}">{msg}</p>
  {/if}

  {#if !acc && !err}
    <p class="pm-empty">Загрузка…</p>
  {:else if acc}
    <section class="dash-zone dash-zone--status" aria-label="Статус клиента">
      <header class="client-detail__head">
        <div>
          <h2 class="client-detail__name">{acc.display_name || acc.telegram_id}</h2>
          <p class="client-detail__tid mono">{acc.telegram_id}</p>
        </div>
        {#if acc.imitation}
          <span class="live-status__tag live-status__tag--pause">имитация</span>
        {/if}
      </header>
      <p class="live-status" class:is-paused={acc.imitation || acc.trading_mode === 'disabled'}>
        <span class="live-status__dot" aria-hidden="true"></span>
        {statusLine}
      </p>
      {#if acc.imitation}
        <p class="pm-empty">Имитация Георга: CLOB-торговлю включить нельзя. Тумблера нет.</p>
      {:else}
        <div class="client-trade" aria-label="Торговля клиента">
          {#if riskNote}
            <p class="client-trade__risk">{riskNote}</p>
          {/if}
          <div class="client-trade__row">
            <span class="client-trade__asset">ETH 15m</span>
            <label
              class="brain-switch"
              title={tradeArmed ? 'Выключить торговлю' : 'Включить торговлю'}
            >
              <input
                type="checkbox"
                role="switch"
                aria-label="Торговля клиента ETH 15m"
                checked={tradeArmed}
                disabled={!!busy}
                on:change={onTradeToggle}
              />
              <span class="brain-switch__ui" aria-hidden="true"></span>
              <span class="brain-switch__txt">{tradeArmed ? 'вкл' : 'выкл'}</span>
            </label>
          </div>
          <p class="client-trade__hint">
            {#if tradeArmed}
              {tradeUnitActive ? 'процесс active' : 'конфиг live, процесс выкл'}
            {:else if trading.halt_active}
              halt (KILL_SWITCH / coin-disabled)
            {:else}
              выкл
            {/if}
            {#if !tradeCanOn}
              · без ключей ON недоступен
            {/if}
          </p>
          <p class="brain-strats__hint">{tradeHint}</p>
          {#if tradeArmed && tradeUnitActive === false}
            <button type="button" class="pm-btn" disabled={!!busy} on:click={onStartUnit}>
              Запустить pmbot-client-live-15m
            </button>
          {/if}
        </div>
      {/if}
    </section>

    <section class="dash-zone" aria-label="Кошелёк и пароль">
      <header class="dash-zone__head">
        <h2>Кошелёк</h2>
      </header>
      <div class="client-wallet">
        <div class="client-wallet__row">
          <span class="client-wallet__dt">EOA</span>
          <span class="client-wallet__dd mono">{wrapAddr(wallet.eoa)}</span>
        </div>
        <div class="client-wallet__row">
          <span class="client-wallet__dt">Funder / proxy</span>
          <span class="client-wallet__dd mono">{wrapAddr(wallet.funder)}</span>
        </div>
        <div class="client-wallet__row">
          <span class="client-wallet__dt">Пароль Mini App</span>
          <span class="client-wallet__dd">{acc.password_set ? 'задан' : 'не задан'}</span>
        </div>
      </div>
      <div class="client-detail__actions">
        {#if !acc.imitation}
          <button
            type="button"
            class="pm-btn pm-btn--warn"
            disabled={!!busy || acc.revoked}
            on:click={onEmergency}
          >
            {confirmStop ? 'Подтвердить отзыв + стоп' : 'Авария: стоп и снять с белого списка'}
          </button>
        {/if}
        <button type="button" class="pm-btn" disabled={!!busy} on:click={onResetPassword}>
          Сбросить пароль Mini App
        </button>
      </div>
    </section>

    <section class="dash-zone" aria-labelledby="zone-balance">
      <header class="dash-zone__head">
        <h2 id="zone-balance">Баланс</h2>
      </header>
      <div class="dash-split">
        <div class="dash-metric" data-horizon={HZ}>
          <p class="dash-metric__label">{HORIZON_TITLES[HZ] || HZ} · pUSD</p>
          <p class="dash-metric__value">
            {bank ? bank.balance : '—'}
            {#if bank}<span class="dash-metric__unit">USD</span>{/if}
          </p>
          <p class="dash-metric__hint">свободно для ставок · кошелёк этого клиента</p>
          <p class="dash-metric__sub">{balanceSub}</p>
        </div>
        <div class="dash-metric" data-asset="pol">
          <p class="dash-metric__label">Газ · POL</p>
          <p class="dash-metric__value">
            {polDisplay != null ? polDisplay : '—'}
            {#if polDisplay != null}<span class="dash-metric__unit">POL</span>{/if}
          </p>
          <p class="dash-metric__sub">{polSub}</p>
        </div>
      </div>
    </section>

    <section class="dash-zone" aria-labelledby="zone-win">
      <header class="dash-zone__head">
        <h2 id="zone-win">% выигрышей</h2>
      </header>
      <div class="dash-split">
        <div class="dash-metric" data-scope="today">
          <p class="dash-metric__label">Сегодня</p>
          <p class="dash-metric__value dash-metric__value--pct">
            {fmtWinRate(liveGlanceToday.win_rate, {
              wins: liveGlanceToday.wins,
              losses: liveGlanceToday.losses,
            })}
          </p>
          <p class="dash-metric__sub">
            {#if liveGlanceToday.wins != null && liveGlanceToday.losses != null && Number(liveGlanceToday.wins) + Number(liveGlanceToday.losses) > 0}
              {liveGlanceToday.wins} побед · {liveGlanceToday.losses} поражений
              {#if fmtRoiWeighted(liveGlanceToday.roi_weighted)}
                · $-ROI {fmtRoiWeighted(liveGlanceToday.roi_weighted)}
              {/if}
            {:else}
              пока мало данных
            {/if}
          </p>
        </div>
        <div class="dash-metric" data-scope="all">
          <p class="dash-metric__label">Всё время</p>
          <p class="dash-metric__value dash-metric__value--pct">
            {fmtWinRate(liveGlanceAll.win_rate, {
              wins: liveGlanceAll.wins,
              losses: liveGlanceAll.losses,
            })}
          </p>
          <p class="dash-metric__sub">
            {#if liveGlanceAll.wins != null && liveGlanceAll.losses != null && Number(liveGlanceAll.wins) + Number(liveGlanceAll.losses) > 0}
              {liveGlanceAll.wins} побед · {liveGlanceAll.losses} поражений
              {#if fmtRoiWeighted(liveGlanceAll.roi_weighted)}
                · $-ROI {fmtRoiWeighted(liveGlanceAll.roi_weighted)}
              {/if}
            {:else}
              пока мало данных
            {/if}
          </p>
        </div>
      </div>
    </section>

    <section class="dash-zone" aria-labelledby="zone-bets">
      <header class="dash-zone__head">
        <h2 id="zone-bets">Ставки</h2>
        <DayPicker
          value={pickDay}
          today={todayId}
          days={calDays}
          month={(cal && cal.month) || liveMonth}
          disabled={dayBusy}
          on:change={onDayChange}
          on:month={onMonthChange}
        />
      </header>
      <div class="dash-bets-summary">
        <p class="dash-bets-summary__bit">
          <span>15m</span>
          <strong>{betCount != null ? betCount : '—'}</strong>
          {#if openStake != null && pickDay === todayId}
            <em>в игре {fmtNum(openStake)}</em>
          {/if}
          {#if dayPctLabel}
            <em
              class="mono"
              class:is-up={daySummary && daySummary.pnl_pct > 0}
              class:is-down={daySummary && daySummary.pnl_pct < 0}>{dayPctLabel}</em
            >
          {/if}
        </p>
      </div>
      {#if dayBusy}
        <p class="pm-empty">Загрузка дня…</p>
      {:else if !recent.length && !live.csv_present}
        <p class="pm-empty">Пока нет PAPER_BET в CSV клиента — пусто нормально до первых попыток.</p>
      {:else}
        <BetFeed items={recent} />
      {/if}
    </section>

    <section class="dash-zone" aria-labelledby="zone-brain">
      <header class="dash-zone__head">
        <h2 id="zone-brain">Мозг и стратегии</h2>
      </header>
      <div class="brain-strats pm-surface" aria-label="Стратегии клиента 15m">
        <div class="brain-strats__bar">
          <h3 class="brain-strats__title">15m · клиент ETH</h3>
          <p class="brain-strats__sum mono">
            {enabledCount}/{rosterCount || stratRows.length} вкл
            {#if totalBets}
              · {totalBets} ставок
            {/if}
            {#if totalSettled}
              · {totalSettled} settle
            {/if}
            {#if stratSource}
              · {stratSource}
            {/if}
          </p>
        </div>
        <p class="brain-strats__hint">
          Yaml и CSV этого telegram_id, не live Георга. Toggle не рестартит pmbot-live-15m.
        </p>
        {#if !yamlOk}
          <p class="pm-empty">{(acc.strategies && acc.strategies.note_ru) || yamlPending}</p>
        {/if}

        {#if stratRows.length === 0}
          <p class="brain-strats__empty">Список стратегий пока пуст.</p>
        {:else}
          <div class="brain-strats__table-wrap">
            <table class="brain-strats__table">
              <thead>
                <tr>
                  <th scope="col" class="tog">Вкл</th>
                  <th scope="col">Стратегия</th>
                  <th scope="col" class="num">Win %</th>
                  <th scope="col" class="num">Ставки</th>
                  <th scope="col" class="num">Settle</th>
                  <th scope="col" class="num">Stake</th>
                  <th scope="col" class="num">PnL</th>
                  <th scope="col" class="stat">Статус</th>
                </tr>
              </thead>
              <tbody>
                {#each stratRows as row (row.id || row.name_ru)}
                  <tr class:is-quiet={!row.bets} class:is-off={!isEnabled(row)}>
                    <td class="tog">
                      {#if row.unattributed || row.id === '_unattributed'}
                        <span class="brain-strats__id mono">—</span>
                      {:else}
                        <label class="brain-switch" title={isEnabled(row) ? 'Выключить' : 'Включить'}>
                          <input
                            type="checkbox"
                            role="switch"
                            aria-label={`${row.name_ru || row.id} client 15m`}
                            checked={isEnabled(row)}
                            disabled={!yamlOk || acc.imitation || !!busy}
                            on:change={() => onToggle(row)}
                          />
                          <span class="brain-switch__ui" aria-hidden="true"></span>
                          <span class="brain-switch__txt">{isEnabled(row) ? 'вкл' : 'выкл'}</span>
                        </label>
                      {/if}
                    </td>
                    <td>
                      <span class="brain-strats__name">{row.name_ru || row.id}</span>
                      <span class="brain-strats__id mono">{row.id}</span>
                    </td>
                    <td class="num mono" class:is-ok={row.hit_pct != null && row.hit_pct >= 55}
                      >{hitTxt(row)}</td
                    >
                    <td class="num mono">{row.bets ?? 0}</td>
                    <td class="num mono muted">{row.n ?? 0}</td>
                    <td class="num mono muted">{stakeTxt(row)}</td>
                    <td class="num mono" class:is-ok={row.pnl > 0} class:is-bad={row.pnl < 0}
                      >{pnlTxt(row)}</td
                    >
                    <td class="stat">
                      <span
                        class="brain-pill"
                        class:is-kill={statusTxt(row) === 'kill'}
                        class:is-off={statusTxt(row) === 'выкл'}>{statusTxt(row)}</span
                      >
                    </td>
                  </tr>
                {/each}
              </tbody>
            </table>
          </div>
        {/if}
      </div>
    </section>

    {#if !acc.imitation}
      <section class="dash-zone" aria-label="CLOB attempts">
        <header class="dash-zone__head">
          <h2>CLOB attempts / rejects</h2>
        </header>
        <p class="pm-meta">Только здесь. Mini App и клиентский бот это не видят.</p>
        {#if !(acc.attempts && acc.attempts.length)}
          <p class="pm-empty">Пока пусто — файла attempts ещё нет.</p>
        {:else}
          <ul class="client-detail__log">
            {#each acc.attempts as a, i (`${a.ts || i}-${a.reason || ''}`)}
              <li class="mono">
                <span>{a.ts || '—'}</span>
                <span>{a.status || (a.accepted ? 'ok' : 'reject')}</span>
                <span>{a.slug || ''}</span>
                <span>{a.reason || ''}</span>
              </li>
            {/each}
          </ul>
        {/if}
      </section>
    {/if}
  {/if}
</div>

<style>
  .dash {
    display: flex;
    flex-direction: column;
    gap: 1.75rem;
  }
  .client-detail__back {
    margin: 0;
    font-size: 0.82rem;
  }
  .client-detail__back a {
    color: var(--pm-ink-soft);
    text-decoration: none;
  }
  .client-detail__back a:hover {
    text-decoration: underline;
  }
  .client-detail__head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 0.55rem;
  }
  .client-detail__name {
    margin: 0;
    font-size: 1.15rem;
    letter-spacing: -0.03em;
  }
  .client-detail__tid {
    margin: 0.15rem 0 0;
    font-size: 0.78rem;
    color: var(--pm-ink-mute);
  }
  .client-detail__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.45rem;
    margin-top: 0.85rem;
  }
  .client-wallet {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.75rem;
    min-width: 0;
  }
  .client-wallet__row {
    display: grid;
    gap: 0.2rem;
    min-width: 0;
  }
  .client-wallet__dt {
    font-size: 0.7rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .client-wallet__dd {
    margin: 0;
    font-size: 0.88rem;
    overflow-wrap: anywhere;
    word-break: break-all;
    min-width: 0;
  }
  .client-detail__log {
    list-style: none;
    margin: 0.4rem 0 0;
    padding: 0;
    display: grid;
    gap: 0.25rem;
    font-size: 0.75rem;
  }
  .client-detail__log li {
    display: grid;
    grid-template-columns: auto auto 1fr 1fr;
    gap: 0.45rem;
    min-width: 0;
  }
  .client-detail__log li span {
    overflow-wrap: anywhere;
    min-width: 0;
  }
  .dash-zone {
    margin: 0;
    padding: 0;
    border: 0;
    background: transparent;
  }
  .dash-zone + .dash-zone {
    padding-top: 1.5rem;
    border-top: 1px solid var(--pm-line);
  }
  .dash-zone--status {
    padding-top: 0;
    border-top: 0;
  }
  .dash-zone__head {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.85rem;
  }
  .dash-zone__head h2 {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .live-status {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.45rem 0.65rem;
    font-size: 0.82rem;
    color: var(--pm-ink-soft);
    line-height: 1.4;
  }
  .live-status__dot {
    width: 0.45rem;
    height: 0.45rem;
    border-radius: 50%;
    background: var(--pm-ink-mute);
    flex-shrink: 0;
  }
  .live-status__tag {
    font-family: var(--pm-mono);
    font-size: 0.62rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--pm-mode-live-fg);
    padding: 0.12rem 0.45rem;
    border-radius: 999px;
    border: 1px solid rgba(var(--pm-live-rgb), 0.35);
    background: rgba(var(--pm-live-rgb), 0.06);
  }
  .live-status__tag--pause {
    color: var(--pm-bad);
    border-color: rgba(192, 57, 43, 0.35);
    background: rgba(192, 57, 43, 0.08);
  }
  .live-status.is-paused {
    color: var(--pm-ink-mute);
  }
  .client-trade {
    margin-top: 0.85rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid var(--pm-line);
    border-radius: 10px;
  }
  .client-trade__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }
  .client-trade__asset {
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.08em;
  }
  .client-trade__hint {
    margin: 0.4rem 0 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
  }
  .client-trade__risk {
    margin: 0 0 0.65rem;
    font-size: 0.82rem;
    line-height: 1.35;
    color: var(--pm-ink);
  }
  .dash-split {
    display: grid;
    gap: 1rem 1.5rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 640px) {
    .dash-split {
      grid-template-columns: 1fr;
    }
  }
  .dash-metric__label {
    margin: 0 0 0.35rem;
    font-size: 0.8rem;
    color: var(--pm-ink-soft);
  }
  .dash-metric__value {
    margin: 0;
    font-size: clamp(1.85rem, 4vw, 2.45rem);
    font-weight: 700;
    letter-spacing: -0.045em;
    line-height: 1;
    color: var(--pm-ink);
    font-variant-numeric: tabular-nums;
  }
  .dash-metric__value--pct {
    letter-spacing: -0.03em;
  }
  .dash-metric__unit {
    margin-left: 0.25rem;
    font-size: 0.38em;
    font-weight: 600;
    letter-spacing: 0.08em;
    color: var(--pm-ink-mute);
    vertical-align: 0.35em;
  }
  .dash-metric__sub {
    margin: 0.45rem 0 0;
    font-size: 0.82rem;
    color: var(--pm-ink-mute);
    font-variant-numeric: tabular-nums;
  }
  .dash-metric__hint {
    margin: 0.35rem 0 0;
    font-size: 0.78rem;
    color: var(--pm-ink-soft);
  }
  .dash-bets-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem 1.25rem;
    margin-bottom: 0.85rem;
  }
  .dash-bets-summary__bit {
    margin: 0;
    display: inline-flex;
    align-items: baseline;
    gap: 0.4rem;
    font-size: 0.82rem;
    color: var(--pm-ink-mute);
  }
  .dash-bets-summary__bit span {
    font-family: var(--pm-mono);
    font-size: 0.68rem;
    letter-spacing: 0.08em;
  }
  .dash-bets-summary__bit strong {
    font-size: 1rem;
    font-weight: 650;
    color: var(--pm-ink);
    font-variant-numeric: tabular-nums;
  }
  .dash-bets-summary__bit em {
    font-style: normal;
    color: var(--pm-ink-mute);
  }
  .dash-bets-summary__bit em.is-up {
    color: var(--pm-ok);
  }
  .dash-bets-summary__bit em.is-down {
    color: var(--pm-bad);
  }
  .brain-strats {
    padding: 0.75rem 0.85rem 0.85rem;
  }
  .brain-strats__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.35rem 1rem;
    margin-bottom: 0.35rem;
  }
  .brain-strats__title {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
  }
  .brain-strats__sum {
    margin: 0;
    font-size: 0.72rem;
    color: var(--pm-ink-mute);
  }
  .brain-strats__hint {
    margin: 0 0 0.55rem;
    font-size: 0.68rem;
    color: var(--pm-ink-mute);
    line-height: 1.35;
  }
  .brain-strats__empty {
    margin: 0;
    font-size: 0.85rem;
    color: var(--pm-ink-mute);
  }
  .brain-strats__table-wrap {
    overflow-x: auto;
  }
  .brain-strats__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;
  }
  .brain-strats__table th {
    text-align: left;
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--pm-ink-mute);
    padding: 0.25rem 0.45rem 0.45rem 0;
    border-bottom: 1px solid var(--pm-line);
  }
  .brain-strats__table th.num,
  .brain-strats__table td.num {
    text-align: right;
  }
  .brain-strats__table th.stat,
  .brain-strats__table td.stat {
    text-align: right;
    padding-right: 0;
  }
  .brain-strats__table th.tog,
  .brain-strats__table td.tog {
    width: 4.5rem;
    padding-right: 0.35rem;
    vertical-align: middle;
  }
  .brain-strats__table td {
    padding: 0.42rem 0.45rem 0.42rem 0;
    border-bottom: 1px solid var(--pm-line);
    vertical-align: baseline;
  }
  .brain-strats__table tr.is-quiet td {
    opacity: 0.72;
  }
  .brain-strats__table tr.is-off td {
    opacity: 0.55;
  }
  .brain-strats__name {
    display: block;
    font-weight: 550;
    color: var(--pm-ink);
    line-height: 1.25;
  }
  .brain-strats__id {
    display: block;
    font-size: 0.64rem;
    color: var(--pm-ink-mute);
    margin-top: 0.1rem;
  }
  .brain-strats__table td.is-ok {
    color: var(--pm-ok);
  }
  .brain-strats__table td.is-bad {
    color: var(--pm-bad);
  }
  .brain-strats__table td.muted {
    color: var(--pm-ink-mute);
  }
  .brain-pill {
    display: inline-block;
    font-size: 0.64rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: lowercase;
    padding: 0.12rem 0.4rem;
    border-radius: 999px;
    border: 1px solid var(--pm-line);
    color: var(--pm-ink-soft);
  }
  .brain-pill.is-kill {
    color: var(--pm-bad);
    border-color: rgba(192, 57, 43, 0.35);
  }
  .brain-pill.is-off {
    color: var(--pm-ink-mute);
  }
  .brain-switch {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    user-select: none;
  }
  .brain-switch input {
    position: absolute;
    opacity: 0;
    width: 1px;
    height: 1px;
    margin: 0;
  }
  .brain-switch__ui {
    width: 1.7rem;
    height: 0.95rem;
    border-radius: 999px;
    background: var(--pm-line);
    border: 1px solid var(--pm-border);
    position: relative;
    flex: 0 0 auto;
  }
  .brain-switch__ui::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 1px;
    width: 0.7rem;
    height: 0.7rem;
    border-radius: 50%;
    background: var(--pm-paper);
    border: 1px solid var(--pm-border);
  }
  .brain-switch input:checked + .brain-switch__ui {
    background: rgba(26, 127, 78, 0.35);
    border-color: rgba(26, 127, 78, 0.45);
  }
  .brain-switch input:checked + .brain-switch__ui::after {
    transform: translateX(0.7rem);
    border-color: rgba(26, 127, 78, 0.5);
  }
  .brain-switch input:disabled + .brain-switch__ui {
    opacity: 0.5;
  }
  .brain-switch__txt {
    font-size: 0.62rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: lowercase;
    color: var(--pm-ink-mute);
    min-width: 1.6rem;
  }
  .mono {
    font-variant-numeric: tabular-nums;
  }
</style>
